import React, { createContext, useContext, useEffect, useState } from "react";
import env from "../services/env";
import { defaultUserMenuItems } from "./userMenu";

// Used until the API answers (or if it fails) so the site never renders empty.
const defaultHeader = [
  { label: "Buy Crypto", href: "/buyCrypto/buyandsell", links: [] },
  { label: "Markets", href: "/markets/overview", links: [] },
  { label: "Trade", links: [["Spot", "/markets/overview"], ["Convert", "/convert"], ["P2P", "/p2ptrading"], ["Copy Trading", "/copy-trading/futures"]] },
  { label: "Futures", links: [["USDⓈ-M Futures", "/futures"], ["Trading Data", "/tradingDataChart"]] },
  { label: "Earn", links: [["Advanced Earn", "/simpleEarn"], ["Staking", "/staking"], ["Deposit", "/depositCrypto"], ["Withdraw", "/wallet"], ["ETH Staking", "/ethStaking"], ["SOL Staking", "/solStaking"]] },
  { label: "Loans", links: [["Loans", "/loan"]] },
  { label: "More", links: [["Affiliate", "/affiliate"], ["Referral", "/referralDashboard"], ["Airdrop", "/airdrop"], ["Support Center", "/support-center"]] },
];

const defaultFooter = [
  { title: "About Us", items: [{ label: "About" }, { label: "Careers" }, { label: "Announcements", url: "/announcement" }, { label: "News" }, { label: "Terms" }, { label: "Privacy" }, { label: "Community" }, { label: "Notices" }, { label: "Downloads" }] },
  { title: "Products", items: [{ label: "Exchange" }, { label: "Buy Crypto" }, { label: "Auto-Invest" }, { label: "ETH Staking" }, { label: "NFT" }, { label: "BABT" }, { label: "Research" }] },
  { title: "Learn", items: [{ label: "Learn & Earn" }, { label: "Bitcoin Price Prediction" }, { label: "Ethereum Price Prediction" }, { label: "Buy Bitcoin" }, { label: "Buy BNB" }, { label: "Buy XRP" }, { label: "Buy Dogecoin" }, { label: "Buy Ethereum" }, { label: "Buy Tradable Altcoins" }] },
  { title: "Support", items: [{ label: "Support Center", url: "/support-center" }, { label: "Product Feedback & Suggestions" }, { label: "Fees" }, { label: "APIs" }, { label: "Blogs", url: "/blogs" }, { label: "Verify" }, { label: "Trading Insight", url: "/trading-insight" }, { label: "Airdrop Portal", url: "/airdrop" }, { label: "Proof of Reserves", url: "/proof-of-reserves" }] },
];

const defaultSocial = [
  { platform: "x", label: "X", url: "https://x.com/TillionX_" },
  { platform: "telegram", label: "Telegram", url: "https://t.me/TillionXOfficial" },
  { platform: "instagram", label: "Instagram", url: "https://www.instagram.com/tillionx/" },
];

const defaults = {
  headerMenus: defaultHeader,
  userMenuItems: defaultUserMenuItems,
  footerColumns: defaultFooter,
  socialLinks: defaultSocial,
  socialLogin: { google: false, apple: false, telegram: false },
  contents: {},
  loaded: false,
};

const SiteConfigContext = createContext(defaults);

const normalize = (data) => {
  const menus = data.menus || {};
  return {
    headerMenus: menus.header?.length
      ? menus.header.map((item) => ({
          label: item.label,
          href: item.url,
          newTab: item.new_tab,
          links: (item.children || []).map((child) => [child.label, child.url, child.new_tab]),
        }))
      : defaultHeader,
    userMenuItems: menus.user?.length
      ? menus.user.map((item) => ({
          name: item.label,
          tab: item.tab,
          url: item.url,
          children: (item.children || []).map((child) => ({ name: child.label, tab: child.tab, url: child.url })),
        }))
      : defaultUserMenuItems,
    footerColumns: menus.footer?.length
      ? menus.footer.map((column) => ({
          title: column.label,
          items: (column.children || []).map((child) => ({ label: child.label, url: child.url, newTab: child.new_tab })),
        }))
      : defaultFooter,
    socialLinks: data.social_links || defaultSocial,
    socialLogin: data.social_login || defaults.socialLogin,
    telegramBot: data.telegram_bot,
    googleClientId: data.google_client_id,
    contents: data.contents || {},
    siteName: data.site_name,
    loaded: true,
  };
};

export const SiteConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaults);

  useEffect(() => {
    fetch(env.url + "/site-config", { headers: { Accept: "application/json" } })
      .then((res) => res.json())
      .then((res) => res?.data && setConfig(normalize(res.data)))
      .catch(() => {});
  }, []);

  return <SiteConfigContext.Provider value={config}>{children}</SiteConfigContext.Provider>;
};

export const useSiteConfig = () => useContext(SiteConfigContext);

// Returns admin-managed text for a key, or the fallback when it is empty.
export const useContent = (key, fallback = "") => {
  const { contents } = useContext(SiteConfigContext);
  return contents[key] || fallback;
};

// FAQs for one category from the admin panel; returns fallback until loaded/if empty.
export const useFaqs = (category, fallback = []) => {
  const [faqs, setFaqs] = useState(fallback);

  useEffect(() => {
    let active = true;
    fetch(`${env.url}/faqs?category=${encodeURIComponent(category)}`, { headers: { Accept: "application/json" } })
      .then((res) => res.json())
      .then((res) => active && Array.isArray(res?.data) && res.data.length && setFaqs(res.data))
      .catch(() => {});
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  return faqs;
};
